<template>
  <div
    v-if="call"
    class="room"
  >
    <div
      v-if="!user"
      class="room__loading"
    >
      <Spinner
        :height="54"
        :width="54"
      />
    </div>
    <div
      v-else
      class="room__wrapper"
    >
      <Avatar
        :width="116"
        :height="116"
        :user="user.user"
        :contact="user.contact"
        :group-info="undefined"
      />
      <div class="room__info">
        <span
          v-if="user.contact"
          class="room__info-name"
        >{{ user.contact?.name }} {{ user.contact?.surname }}</span>
        <span
          v-else
          class="room__info-name"
        >{{ user.user.name }} {{ user.user.surname }}</span>
        <div
          v-if="!call.isSecret"
          class="room__info-status"
        >
          {{ call.call_type === 'video' ? 'Входящий видеозвонок' : 'Входящий аудиозвонок' }}
        </div>
        <div
          v-else
          class="room__info-status"
        >
          🔒 {{ call.call_type === 'video' ? 'Секретный видеозвонок' : 'Секретный аудиозвонок' }}
        </div>
        <p
          v-if="secretMessage"
          class="cSecretCallIncoming__note"
        >
          {{ secretMessage }}
        </p>
      </div>
      <div class="room__actions">
        <button
          v-if="canAccept"
          class="room__actions-button"
          :disabled="accepting"
          @click="accept"
        >
          <div class="room__actions-button-icon --green">
            <IconAnswerCall
              :width="26"
              :height="26"
            />
          </div>
          <span>Принять</span>
        </button>
        <button
          class="room__actions-button"
          @click="reject"
        >
          <div class="room__actions-button-icon --red">
            <IconEndCall
              :width="26"
              :height="26"
            />
          </div>
          <span>Отклонить</span>
        </button>
        <button
          v-if="secretCheck?.status === 'other_device'"
          class="room__actions-button"
          @click="emit('close')"
        >
          <div class="room__actions-button-icon cSecretCallIncoming__close">
            ✕
          </div>
          <span>Закрыть</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, defineProps, defineEmits, ref, watch} from 'vue'
import { useRouter } from 'vue-router'
import { CallApi } from '@/shared/api/methods/CallApi'
import { CallsStore, CallInfo } from '@/store/modules/Calls/CallsStore'
import Avatar from '@/shared/components/Avatar/Avatar.vue'
import Spinner from '@/shared/components/Spinner/Spinner.vue'
import IconEndCall from '@/shared/icons/IconEndCall.vue'
import IconAnswerCall from '@/shared/icons/IconAnswerCall.vue'
import {ChatsStore} from '@/store/modules/Chats/ChatsStore'
import {Cookies} from '@/shared/helpers/Cookies'
import {ContactsStore} from '@/store/modules/Contacts/ContactsStore'
import { ringtone } from '@/composables/useRingtone'
import {onBeforeMount, onBeforeUnmount} from 'vue-demi'
import { callForCookie } from '@/store/modules/Calls/CallsStore'
import {
  checkIncomingSecretCall,
  handleSecretCallError,
  trustPeerKeyFromEnvelope,
  type IncomingSecretCallCheck,
} from '@/shared/calls/secretCall'
import { SECRET_CALL_TEXTS } from '@/shared/calls/secretCallTexts'

const props = defineProps<{ call: CallInfo }>()
const emit = defineEmits(['close'])

const router = useRouter()
const {callStore, callState} = CallsStore.get()
const {chatsState} = ChatsStore.get()
const {contactsState} = ContactsStore.get()

const cookieOptions = {
  domain: window.location.hostname,
  path: '/',
}
// const {requestMediaPermissions} = liveKit
const user = computed(() => chatsState.chats.find(item => item.id === props.call?.chatId) || contactsState.contacts.find(item => item.chat_id === props.call?.chatId))

// --- Секретный звонок: проверка конверта ДО кнопки «Принять» (E2E-CALLS.md, §3.6) ---
const secretCheck = ref<IncomingSecretCallCheck | null>(null)
const accepting = ref(false)

watch(
  () => props.call && props.call.isSecret ? `${props.call.callId}` : null,
  async (id) => {
    secretCheck.value = null
    if (!id) return
    const c = props.call
    secretCheck.value = await checkIncomingSecretCall(+c.callId, +c.chatId, +c.callerId, +c.userId, c.keyEnvelope)
  },
  { immediate: true },
)

const canAccept = computed(() => !props.call?.isSecret || secretCheck.value?.status === 'ok')

const secretMessage = computed(() => {
  if (!props.call?.isSecret) return ''
  const check = secretCheck.value
  if (!check) return 'Проверяем ключ звонка…'
  if (check.status === 'other_device') return SECRET_CALL_TEXTS.otherDevice
  if (check.status === 'failed') return SECRET_CALL_TEXTS.keyFailed
  return check.keyChanged ? SECRET_CALL_TEXTS.keyChanged : ''
})

async function accept() {
  if (accepting.value || !canAccept.value) return
  accepting.value = true
  try {
    await acceptCall()
  } finally {
    accepting.value = false
  }
}

async function acceptCall() {
  // await requestMediaPermissions('video')
  await navigator.mediaDevices.getUserMedia({ video: true })
      .then(stream => stream.getTracks().forEach(track => track.stop()))
      .catch(err => console.warn('Permissions denied', err))
  await navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => stream.getTracks().forEach(track => track.stop()))
      .catch(err => console.warn('Permissions denied', err))

  const check = secretCheck.value
  const secret = props.call.isSecret && check?.status === 'ok' ? check : null

  let response: any
  try {
    response = await CallApi.accept(props.call.chatId, {
      call_id: +props.call.callId,
      ...(secret ? { e2e_public_key: secret.devicePublicKey } : {}),
    })
  } catch (e) {
    if (await handleSecretCallError(e) !== 'unhandled') return
    throw e
  }
  emit('close')

  // Пользователь принял звонок, видя предупреждение о смене ключа — доверяем новому.
  if (secret?.keyChanged) {
    await trustPeerKeyFromEnvelope(+props.call.callerId, secret.envelope)
  }

  await callStore.dispatch(async (state) => {
    state.activeCall = {
      ...props.call,
      status: 'active',
      liveKitRoomName: response.room_name,
      liveKitToken: response.token,
      liveKitUrl: response.url,
      date: new Date(),
      ...(secret ? { isSecret: true, keyEnvelope: secret.envelope, e2eKey: secret.key } : {}),
    }
    state.incomingCall = null
  })
  Cookies.set('call', callForCookie(callState.activeCall), cookieOptions)
  ringtone.stopRingtone()
  await router.push('/call/active')
}

async function reject() {
  try {
    await CallApi.reject(props.call.chatId, {call_id: +props.call.callId})
  } catch (e) {
    console.error('reject error', e)
  }

  await callStore.dispatch(async (state) => {
    // state.activeCall = null
    state.incomingCall = null
  })
  const cookieOptions = {
    domain: window.location.hostname,
    path: '/',
  }
  Cookies.remove('call', cookieOptions)
  ringtone.stopRingtone()

  emit('close')
}

onBeforeMount(() => {
  if (ringtone.isEnabled) {
    ringtone.enableSound()
  }
})
onBeforeUnmount(() => {
  ringtone.stopRingtone()
})
</script>

<style>
/* Секретный звонок: префикс уникален, стили в проекте глобальные. */
.cSecretCallIncoming__note {
  max-width: 320px;
  margin: 8px auto 0;
  font-size: 14px;
  line-height: 1.4;
  color: var(--color-text-dark);
  opacity: 0.8;
}
.room__actions-button-icon.cSecretCallIncoming__close {
  background: var(--color-text-light);
  color: #fff;
  font-size: 22px;
}
</style>

<style scoped>
.room {
  position: fixed;
  z-index: 2000;
  display: flex;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-gray);
}
.room__loading .cSpinner div {
  border-color: var(--color-primary) transparent transparent transparent;
}
.room__wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.room__info {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  gap: 4px;
}
.room__info-name {
  font-family: var(--font-family-headers);
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
  text-align: center;
  color: var(--color-text-dark);
}
.room__info-status {
  font-family: var(--font-family-text);
  color: var(--color-primary);
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  display: flex;
  width: auto;
  justify-content: center;
  align-items: center;
  gap: 7px;
}

.room__actions {
  display: flex;
  width: auto;
  gap: 32px;
  margin-top: 60px;
  align-items: center;
  justify-content: space-between;
}

.room__actions-button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: unset;
  background: unset;
  gap: 4px;
  font-family: var(--font-family-text);
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  cursor: pointer;
  color: var(--color-text-light);
  &:hover {
    opacity: 0.8;
  }
}

.room__actions-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
}
.--red {
  background: var(--color-secondary-red);
}
.--green {
  background: var(--color-secondary-green);
  position: relative;
  div {
    z-index: 2;
  }
  &:before {
    content: "";
    position: absolute;
    background: var(--color-secondary-green);
    opacity: 0.2;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    z-index: 0;
    animation: pulse 1s infinite ease-out;
  }
  &:after {
    content: "";
    position: absolute;
    background: var(--color-secondary-green);
    opacity: 0.1;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    z-index: 0;
    animation: pulse 1s infinite ease-out;
  }
}
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.8);
  }
  100% {
    transform: scale(1);
  }
}

</style>